import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { UserApi } from "../../../../../../../../services/Api/types";
import { api } from "../../../../../../../../services/Security/api";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import ChangeRoleModal from "../../components/ChangeRoleModal";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "user";
const resourceType = "user";

const List = ({ resource }: ListState) => {
	const [items, setItems] = useState<UserApi[]>([]);
	const [params, setParams] = useState<Record<string, any>>({});
	const [userToChange, setUserToChange] = useState<undefined | UserApi>(undefined);
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRemoveUser = async (item: UserApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.removeUser(item.id, { reason: "" });
		addSuccessFlash(t("lib:common.flash.removed"));
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await adminApi.removeUser(item.id, { reason: "" });
			setItems(items.filter((dataItem) => dataItem.id !== item.id));
			addSuccessFlash(t("lib:common.flash.removed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onChangeRole = async (user: UserApi) => setUserToChange(user);

	const handleUpdateRoles = async (user?: UserApi) => {
		setUserToChange(undefined);
		if (user) {
			try {
				const res = await adminApi.getAdminUsers(params);
				setItems(res);
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "email",
				name: t("lib:common.word.email"),
				type: "text",
			},
			{
				id: "phone_number",
				name: t("common.word.phone_number", { ns: "lib" }),
				type: "text",
			},
			{
				id: "roles",
				name: t("modules.user.field.roles.title"),
				render: (item: UserApi) => item.roles.map((role) => t(`enums.roles.${role}`)).join(", "),
				disableSorting: true,
			},
			{
				id: "organizationIds",
				name: t("lib:common.word.locations"),
				type: "search_select",
				disableSorting: true,
				render: (data: UserApi) => {
					return (
						<RenderLimitedText minWidth={400}>
							{data.organizations.map((org, index) => {
								return (
									<>
										<Link to={`/admin/organizations/${org.id}`} target={"_blank"}>
											{org.name}
										</Link>
										{index < data.organizations.length - 1 && ", "}
									</>
								);
							})}
						</RenderLimitedText>
					);
				},
				renderExport: (data: UserApi) => {
					return data.organizations.map((organization: { name: string }) => organization.name).join(", ");
				},
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.user().getOrganizationsSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
		],
		filters: [
			{
				id: "role",
				name: t("modules.user.field.role.title"),
				type: "list",
				options: {
					USER: t("enums.roles.USER"),
					POSNET: t("enums.roles.POSNET"),
					ADMIN: t("enums.roles.ADMIN"),
					SUPPORT: t("enums.roles.SUPPORT"),
					IT_SUPPORT: t("enums.roles.IT_SUPPORT"),
					OFFICE: t("enums.roles.OFFICE"),
					SALE: t("enums.roles.SALE"),
				},
			},
		],
		actions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: UserApi) => onRemoveUser(item),
				refreshListAfter: true,
			},
			{
				name: t("common.action.change_role"),
				click: (item: UserApi) => onChangeRole(item),
			},
		],
		selectedColumns: ["name", "email", "phone_number", "roles", "organizationIds"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "organization,roles";
			setParams(params);
			return adminApi.getAdminUsers(params, { cancelToken: sourceToken?.token });
		},
		exportConfig: {
			title: t("common.word.users", { ns: "lib" }),
			filename: t("common.word.users", { ns: "lib" }),
			organization: organization.name,
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return (
		<>
			{userToChange && <ChangeRoleModal user={userToChange} onHide={handleUpdateRoles} />}
			<ListData data={items} config={config} onFetch={(items: UserApi[]) => setItems(items)} />
		</>
	);
};

const AdminUsersList = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("common.word.users", { ns: "lib" })} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("common.word.users", { ns: "lib" })} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default AdminUsersList;
